import {
  parseMinKNOWPath,
  isValidSampleDirectory,
  FASTQ_PATTERN,
  CLASSIC_FASTQ_PATTERN,
  findSupportingFiles,
  parseSampleSheet,
  parseFinalSummary
} from './minKNOWDirectoryUtils';

const SKIP_STATUSES = new Set(['analyzing', 'completed', 'succeeded', 'failed', 'delayed']);

export function buildError(code, message, extra = {}) {
  const error = new Error(message || code);
  error.code = code;
  Object.assign(error, extra);
  return error;
}

export const normalizePath = (file) =>
  (file?.webkitRelativePath || file?.path || file?.name || '').replace(/\\/g, '/');

function findMetadataDirectoryName(segments) {
  const parsedSegment = segments.find((segment) => parseMinKNOWPath(segment));
  if (parsedSegment) {
    return parsedSegment;
  }

  const fastqPassSubdirectoryIndex = segments.findIndex((segment) => segment.toLowerCase() === 'fastq_pass');
  if (fastqPassSubdirectoryIndex > 0) {
    return segments[fastqPassSubdirectoryIndex - 1];
  }

  return segments[0] || '';
}

function findMetadataDirectoryNameForFile(file) {
  const segments = normalizePath(file).split('/').filter((segment) => segment.length > 0);
  return findMetadataDirectoryName(segments);
}

export async function extractSupportingMetadata(files) {
  const { sampleSheetFile, finalSummaryFile } = findSupportingFiles(files);
  const metadata = {};

  if (sampleSheetFile?.text) {
    try {
      const csvContent = await sampleSheetFile.text();
      const sampleSheetMetadata = parseSampleSheet(csvContent);
      if (sampleSheetMetadata?.flowCellId) {
        metadata.flowCellSerialNumber = sampleSheetMetadata.flowCellId;
      }
      if (sampleSheetMetadata?.flowCellProductCode) {
        metadata.flowCellProductCode = sampleSheetMetadata.flowCellProductCode;
      }
    } catch (error) {
      // Ignore supporting file parsing errors to keep uploads resilient.
    }
  }

  if (finalSummaryFile?.text) {
    try {
      const summaryContent = await finalSummaryFile.text();
      const summaryMetadata = parseFinalSummary(summaryContent);
      if (summaryMetadata?.flowCellId) {
        metadata.flowCellSerialNumber = summaryMetadata.flowCellId;
      }
      if (summaryMetadata?.sequencerSerialNumber) {
        metadata.sequencerSerialNumber = summaryMetadata.sequencerSerialNumber;
      }
    } catch (error) {
      // Ignore supporting file parsing errors to keep uploads resilient.
    }
  }

  return metadata;
}

export async function extractMetadataFromFiles(files) {
  const rootDirectoryName = findMetadataDirectoryNameForFile(files[0]);
  const metadata = rootDirectoryName ? parseMinKNOWPath(rootDirectoryName) : null;
  const supportingMetadata = await extractSupportingMetadata(files);

  if (!metadata && !supportingMetadata.flowCellSerialNumber && !supportingMetadata.sequencerSerialNumber) {
    throw buildError('INVALID_PATH_STRUCTURE', 'Invalid Gourami directory path structure.');
  }

  const mergedMetadata = {
    ...(metadata || {}),
    ...supportingMetadata
  };

  if (!mergedMetadata.datasetName && rootDirectoryName) {
    mergedMetadata.datasetName = rootDirectoryName;
  }

  return mergedMetadata;
}

export function groupFilesBySample(files, metadata, rootDirectoryName) {
  const sampleMap = new Map();

  for (const file of files) {
    const relativePath = normalizePath(file);
    const segments = relativePath.split('/');
    const fastqPassSubdirectoryIndex = segments.findIndex((segment) => segment.toLowerCase() === 'fastq_pass');
    if (fastqPassSubdirectoryIndex === -1 || fastqPassSubdirectoryIndex + 1 >= segments.length) {
      continue;
    }

    const fileRootDirectoryName = findMetadataDirectoryName(segments);
    if (rootDirectoryName && fileRootDirectoryName && fileRootDirectoryName !== rootDirectoryName) {
      throw buildError('INVALID_FASTQ_STRUCTURE', 'Selected files span multiple Gourami run directories.');
    }

    const filename = segments[segments.length - 1];

    const fastqMatch = FASTQ_PATTERN.exec(filename);
    if (!fastqMatch) {
      continue;
    }

    const sampleName = segments[fastqPassSubdirectoryIndex + 1];

    if (!isValidSampleDirectory(sampleName)) {
      throw buildError('INVALID_FASTQ_STRUCTURE', 'Invalid sample directory name.');
    }

    if (metadata?.format === 'classic' && metadata.flowCellSerialNumber) {
      const classicMatch = CLASSIC_FASTQ_PATTERN.exec(filename);
      if (!classicMatch) {
        throw buildError('INVALID_FASTQ_STRUCTURE', 'Invalid FASTQ filename structure.');
      }
      if (classicMatch[1] !== metadata.flowCellSerialNumber) {
        throw buildError('INVALID_FASTQ_STRUCTURE', 'FASTQ filename flow cell does not match directory metadata.');
      }
      if (classicMatch[2].toLowerCase() !== sampleName.toLowerCase()) {
        throw buildError('INVALID_FASTQ_STRUCTURE', 'FASTQ filename barcode does not match sample directory.');
      }
    }

    const sampleEntry = sampleMap.get(sampleName) || { name: sampleName, files: [] };
    sampleEntry.files.push(file);
    sampleMap.set(sampleName, sampleEntry);
  }

  const sampleEntries = Array.from(sampleMap.values()).filter((entry) => entry.files.length > 0);
  if (!sampleEntries.length) {
    throw buildError('INVALID_FASTQ_STRUCTURE', 'No valid FASTQ files found in sample directories.');
  }

  return sampleEntries;
}

export async function validateDirectoryStructure(files = []) {
  if (!files.length) {
    throw buildError('DIRECTORY_NOT_FOUND', 'The selected Gourami directory does not contain FASTQ files.');
  }

  const rootDirectoryName = findMetadataDirectoryNameForFile(files[0]);
  const metadata = await extractMetadataFromFiles(files);
  const sampleEntries = groupFilesBySample(files, metadata, rootDirectoryName);

  return { metadata, sampleEntries };
}

export function buildGouramiDatasetAttributes(metadata = {}) {
  return {
    sequencing_platform: 'ont',
    ...(metadata.flowCellSerialNumber ? { flowcell_serial_number: metadata.flowCellSerialNumber } : {}),
    ...(metadata.sequencerSerialNumber ? { minion_sn: metadata.sequencerSerialNumber } : {}),
    ...(metadata.flowCellProductCode ? { flowcell_name: metadata.flowCellProductCode } : {})
  };
}

export function shouldSkipAnalysisStatus(status) {
  return SKIP_STATUSES.has(status);
}
